import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';

import { type OfferCodeRefusalProps } from './OfferCodeRefusal.types';

/**
 * A refused write, drawn where the operator is looking.
 *
 * **Inside the dialog and never a toast**: the operator is mid-form, and a
 * notification that slides away takes the only explanation with it.
 *
 * **One refusal, two recoveries.** A 409 means a colleague mapped this rule,
 * plan and store first and is **right** — so the offer is a re-read. A 404 means
 * the mapping is gone and there is nothing to go and read, so the offer is to
 * close. Anything else is a sentence and no button, because there is no second
 * step to take.
 *
 * A sibling of Payments' `DecisionRefusal` rather than an import of it: a module
 * never reaches into another's components (W6), and this one speaks the
 * `subscriptions` namespace.
 */
export function OfferCodeRefusal({
  message,
  isConflict,
  isGone,
  onReload,
  onClose,
}: OfferCodeRefusalProps) {
  const { t } = useTranslation('subscriptions');

  if (message === null) {
    return null;
  }

  return (
    <div role="alert" className="flex flex-col gap-sm rounded-md border border-destructive p-md">
      <p className="flex items-start gap-sm text-body text-destructive">
        <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{message}</span>
      </p>

      {isConflict || isGone ? (
        <div>
          <Button
            variant="secondary"
            onClick={() => {
              if (isGone) {
                onClose();
              } else {
                onReload();
              }
            }}
          >
            {t(isGone ? 'offerCodes.error.close' : 'offerCodes.error.reread')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
